import { MoreVert, Visibility } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetShopConfig } from '../../../services/core/settings';
import { truncateWithEllipsis } from '../../../utility/helpers/commonFunction';
import EmptyAcceptedAppSvg from '../../../utility/Svg/EmptyAcceptedAppSvg';
import EmptyDeclineAppSvg from '../../../utility/Svg/EmptyDeclineAppSvg';
import EmptyExpiredAppSvg from '../../../utility/Svg/EmptyExpiredAppSvg';
import EmptyRescheduleAppSvg from '../../../utility/Svg/EmptyRescheduleAppSvg';
import PendingRequestSvg from '../../../utility/Svg/PendingRequestSvg';
import { formatPhoneNumber } from '../../Common/FormatPhoneNumber';

// ── Style constants ────────────────────────────────────────────────────────
const TABLE_SX = { tableLayout: 'auto', width: '100%', borderRadius: '8px' };
const ACTION_COL = { width: 80, minWidth: 80, maxWidth: 80 };
const ACTION_COL_100 = { width: 117, minWidth: 117, maxWidth: 117 };
const COL = {};
const BODY_CELL = { whiteSpace: 'nowrap' };

const styles = {
    tableContainerMdUp: { overflowX: 'auto', overflowY: 'visible', maxHeight: 'none', width: '100%' },
    tableContainerSmDown: { overflow: 'auto', maxHeight: 500, width: '100%' },
    actionColHeader: { ...ACTION_COL, fontWeight: 500 },
    actionColHeader100: { ...ACTION_COL_100, fontWeight: 500 },
    emptyCellLoader: { py: 6 },
    emptyTitle: { marginTop: '20px', fontSize: '42px' },
    emptySubtitle: {
        color: 'rgba(0, 0, 0, 0.60)',
        maxWidth: 536,
        fontSize: '18px',
        fontWeight: 400,
        letterSpacing: '0.17px',
        lineHeight: '143%',
        textAlign: 'center',
        mt: '20px !important',
        mb: '4px !important',
    },
    actionIconBtn: { background: '#EBEBEB', width: '24px', height: '24px' },
    actionIcon: { width: '18px', height: '18px' },
    menuPaper: {
        borderRadius: '4px!important',
        boxShadow: '0 3px 14px 2px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.14), 0 5px 5px -3px rgba(0,0,0,0.20)',
    },
    menuItemIcon: { marginLeft: '8px' },
    menuItem: { padding: '6px 8px!important' },
    statusChip: (bg, color) => ({
        backgroundColor: bg,
        color,
        height: 36,
        padding: '8px 0',
        fontWeight: 500,
        fontSize: '13px',
        letterSpacing: '0.15px',
        borderRadius: '100px',
    }),
    typeText: {
        color: '#006CDD',
        fontWeight: 400,
        letterSpacing: '0.17px',
        lineHeight: '143%',
    },
    reviewBtn: {
        width: '85px!important',
        height: '36px!important',
        padding: '4px 10px!important',
        borderRadius: '4px',
        fontSize: '13px',
        letterSpacing: '0.46px',
        boxShadow: '0 1px 5px 0 rgba(0,0,0,0.12), 0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.20)',
    },
};

// ── Accepted status maps ───────────────────────────────────────────────────
const ACCEPTED_STATUS_CHIP = {
    in_progress: { bg: '#FD8600', color: 'rgba(0, 0, 0, 0.87)' },
    waiting_for_drop_off: { bg: '#75DBFF', color: 'rgba(0, 0, 0, 0.87)' },
    vehicle_dropped_off: { bg: '#FFC30F', color: 'rgba(0, 0, 0, 0.87)' },
    mark_as_no_show: { bg: '#F3554A', color: 'rgba(0, 0, 0, 0.87)' },
    ready_for_pick_up: { bg: '#5CE469', color: 'rgba(0, 0, 0, 0.87)' },
    customer_paid: { bg: '#00CD44', color: 'rgba(0, 0, 0, 0.87)' },

    cancelled_by_shop_owner: { bg: '#F3554A', color: 'rgba(0, 0, 0, 0.87)' },
    cancelled_by_customer: { bg: '#F3554A', color: 'rgba(0, 0, 0, 0.87)' },
};

const getAcceptedStatusStyle = (status) => {
    const key = (status || '').toLowerCase().replace(/ /g, '_');
    return ACCEPTED_STATUS_CHIP[key] || { bg: '#EEEEEE', color: 'rgba(0, 0, 0, 0.87)' };
};

const getAcceptedStatusLabel = (status) => {
    const map = {
        in_progress: 'In Progress',
        waiting_for_drop_off: 'Waiting for Drop off',
        vehicle_dropped_off: 'Vehicle Dropped Off',
        mark_as_no_show: 'No show',
        ready_for_pick_up: 'Ready for pickup',
        customer_paid: 'Customer Paid',

        cancelled_by_shop_owner: 'Cancelled',
        cancelled_by_customer: 'Cancelled',
    };
    const key = (status || '').toLowerCase().replace(/ /g, '_');
    return map[key] || status;
};

const getTypeLabel = (type) => {
    const map = { today: 'Today', upcoming: 'Upcoming', past: 'Past' };
    return map[(type || '').toLowerCase()] || type || '—';
};

// ── Per-tab column definitions ─────────────────────────────────────────────
const TAB_COLUMNS = {
    0: [
        { id: 'appointmentNumber', label: 'Appointment ID', sortable: true, minWidth: 132 },
        { id: 'dropOffDateTime', label: 'Drop Off Date', sortable: true, minWidth: 125 },
        { id: 'customer.fullName', label: 'Customer Name', sortable: true },
        { id: 'phoneNumber', label: 'Phone Number', minWidth: 130 },
        { id: 'vehicle', label: 'Vehicle', minWidth: 160 },
        { id: 'services', label: 'Services' },
    ],
    1: [
        { id: 'appointmentNumber', label: 'Appointment ID', sortable: true, minWidth: 132 },
        { id: 'dropOffDateTime', label: 'Drop Off Date', sortable: true, minWidth: 125 },
        { id: 'customer.fullName', label: 'Customer Name', sortable: true },
        { id: 'phoneNumber', label: 'Phone Number', minWidth: 130 },
        { id: 'vehicle', label: 'Vehicle', minWidth: 150 },
        { id: 'services', label: 'Services' },
        { id: 'type', label: 'Type' },
        { id: 'status', label: 'Status' },
    ],
    2: [
        { id: 'appointmentNumber', label: 'Appointment ID', sortable: true },
        { id: 'oldDropOff', label: 'Old Drop Off' },
        { id: 'newDropOff', label: 'New Drop Off' },
        { id: 'customer.fullName', label: 'Customer Name', sortable: true },
        { id: 'phoneNumber', label: 'Phone Number' },
        { id: 'vehicle', label: 'Vehicle', minWidth: 160 },
        { id: 'services', label: 'Services' },
    ],
    3: [
        { id: 'appointmentNumber', label: 'Appointment ID', sortable: true },
        { id: 'createdAt', label: 'Date Requested', sortable: false },
        { id: 'dropOffDateTime', label: 'Drop Off Date', sortable: true },
        { id: 'customer.fullName', label: 'Customer Name', sortable: true },
        { id: 'phoneNumber', label: 'Phone Number' },
        { id: 'vehicle', label: 'Vehicle', minWidth: 160 },
        { id: 'services', label: 'Services' },
    ],
    4: [
        { id: 'appointmentNumber', label: 'Appointment ID', sortable: true },
        { id: 'createdAt', label: 'Date Requested', sortable: false },
        { id: 'dropOffDateTime', label: 'Drop Off Date', sortable: true },
        { id: 'customer.fullName', label: 'Customer Name', sortable: true },
        { id: 'phoneNumber', label: 'Phone Number' },
        { id: 'vehicle', label: 'Vehicle', minWidth: 160 },
        { id: 'services', label: 'Services' },
    ],
};

// ── Cell renderer ──────────────────────────────────────────────────────────
const renderCell = (colId, row) => {
    switch (colId) {
        case 'appointmentNumber':
            return row?.appointmentId || row?.appointmentNumber || '—';
        case 'createdAt':
            return row?.createdAt ? moment(row.createdAt).format('DD-MMM-YYYY') : '—';
        case 'dropOffDateTime':
            return row?.dropOffDateTime
                ? moment(row.dropOffDateTime).format('DD-MMM-YYYY')
                : row?.proposedDate
                  ? moment(row.proposedDate).format('DD-MMM-YYYY')
                  : '—';
        case 'customer.fullName':
            return row?.customer?.fullName || `${row?.customer?.firstName || ''} ${row?.customer?.lastName || ''}`.trim() || '—';
        case 'phoneNumber':
            return formatPhoneNumber(row?.customer?.phoneNumber) || formatPhoneNumber(row?.phoneNumber) || '—';
        case 'oldDropOff':
            return row?.oldDropOffDate
                ? moment(row.oldDropOffDate).format('DD-MMM-YYYY | hh:mm A')
                : row?.oldDropOffDateTime
                  ? moment(row.oldDropOffDateTime).format('DD-MMM-YYYY | hh:mm A')
                  : '—';
        case 'newDropOff':
            return row?.newDropOffDateTime ? moment(row.newDropOffDateTime).format('DD-MMM-YYYY | hh:mm A') : '—';
        case 'vehicle':
            return row?.vehicle ? [row.vehicle.year, row.vehicle?.make?.name, row.vehicle?.model?.name].filter(Boolean).join(' ') || '—' : '—';
        case 'services':
            if (Array.isArray(row?.services)) {
                if (row.serviceCount > 1) return `${row.services.length} Services`;
                if (row.serviceCount === 1) return truncateWithEllipsis(row.services[0]?.name) || '1 Service';
            }
            return row?.serviceName || '—';
        case 'type': {
            const typeLabel = getTypeLabel(row?.appointmentType);
            return (
                <Typography variant="body2" sx={styles.typeText}>
                    {typeLabel}
                </Typography>
            );
        }
        case 'status': {
            if (!row?.status) return '—';
            let chipStyle;
            if (row?.requestStatus === 'cancelled_by_shop_owner' || row?.requestStatus === 'cancelled_by_customer') {
                chipStyle = getAcceptedStatusStyle(row.requestStatus);
            } else {
                chipStyle = getAcceptedStatusStyle(row.status);
            }

            return (
                <Chip
                    label={getAcceptedStatusLabel(
                        row?.requestStatus === 'cancelled_by_shop_owner' || row?.requestStatus === 'cancelled_by_customer' ? row.requestStatus : row.status
                    )}
                    sx={styles.statusChip(chipStyle.bg, chipStyle.color)}
                />
            );
        }
        default:
            return '—';
    }
};

// ── View Appointment SVG icon ──────────────────────────────────────────────
const ViewAppointmentIcon = () => (
    <div style={{ width: '24px', height: '24px' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
                d="M15.8333 2.50016H12.35C12 1.5335 11.0833 0.833496 10 0.833496C8.91667 0.833496 8 1.5335 7.65 2.50016H4.16667C3.25 2.50016 2.5 3.25016 2.5 4.16683V17.5002C2.5 18.4168 3.25 19.1668 4.16667 19.1668H15.8333C16.75 19.1668 17.5 18.4168 17.5 17.5002V4.16683C17.5 3.25016 16.75 2.50016 15.8333 2.50016ZM10 2.50016C10.4583 2.50016 10.8333 2.87516 10.8333 3.3335C10.8333 3.79183 10.4583 4.16683 10 4.16683C9.54167 4.16683 9.16667 3.79183 9.16667 3.3335C9.16667 2.87516 9.54167 2.50016 10 2.50016ZM15.8333 17.5002H4.16667V4.16683H5.83333V6.66683H14.1667V4.16683H15.8333V17.5002Z"
                fill="black"
            />
        </svg>
    </div>
);

// ── Empty state config ─────────────────────────────────────────────────────
// `Icon` is a component REFERENCE (PascalCase), NOT pre-rendered JSX.
// Pre-rendered JSX like `<PendingRequestSvg />` at module scope causes React's
// "Maximum update depth exceeded" error because the element object is created
// once outside any render cycle and React loops trying to reconcile it.
const EMPTY_STATE_CONFIG = {
    0: {
        title: 'No Pending Requests',
        subtitle: 'When customers book through the Apex Auto App or your website their requests will appear here.',
        buttonText: 'View online booking link',
        action: (configData) => {
            const link = `${process.env.REACT_APP_ONLINE_WEB_URL}${configData?.registeredUrl}`;
            window.open(link, '_blank');
        },
        Icon: PendingRequestSvg,
    },
    1: {
        title: 'No Accepted Appointments',
        subtitle: "Appointments you've accepted will appear here. Accept an outstanding request to get started.",
        buttonText: 'View appointments',
        action: (configData, navigate) => {
            navigate('/appointment/today');
        },
        Icon: EmptyAcceptedAppSvg,
    },
    2: {
        title: 'No Reschedule Requests',
        subtitle: 'When you propose a new time for an appointment request, those appointments will be displayed here. ',
        buttonText: '',
        action: () => {},
        Icon: EmptyRescheduleAppSvg,
    },
    3: {
        title: 'No Declined Requests',
        subtitle: 'Appointments you’ve declined will be displayed here.',
        buttonText: '',
        action: () => {},
        Icon: EmptyDeclineAppSvg,
    },
    4: {
        title: 'No Expired Requests',
        subtitle: 'No declined appointment requests found.',
        buttonText: '',
        action: () => {},
        Icon: EmptyExpiredAppSvg,
    },
};

// ── EmptyState: a proper React component so hooks/render rules are respected ──
const EmptyState = ({ tabValue, configData, navigate }) => {
    const { title, subtitle, buttonText, action, Icon } = EMPTY_STATE_CONFIG[tabValue] || EMPTY_STATE_CONFIG[0];

    return (
        <Stack alignItems="center" spacing={2}>
            {Icon && <Icon />}
            <Typography variant="h4" fontWeight={500} style={styles.emptyTitle}>
                {title}
            </Typography>
            <Typography variant="h6" sx={styles.emptySubtitle}>
                {subtitle}
            </Typography>
            {buttonText && (
                <Button variant="outlined" color="primary" onClick={() => action(configData, navigate)}>
                    {buttonText}
                </Button>
            )}
        </Stack>
    );
};

// ── EmptyState: a proper React component so hooks/render rules are respected ──
const EmptyResult = () => {
    return (
        <Stack alignItems="center" spacing={2}>
            <svg width="150" height="150" viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_24531_66228)">
                    <path
                        d="M135.973 128.608L100.426 93.0616C107.311 84.5566 111.457 73.7493 111.457 61.9787C111.457 34.6978 89.2594 12.5 61.9785 12.5C34.6975 12.5 12.5 34.6975 12.5 61.9785C12.5 89.2594 34.6978 111.457 61.9787 111.457C73.7493 111.457 84.5566 107.311 93.0616 100.426L128.608 135.973C129.624 136.988 130.957 137.499 132.29 137.499C133.624 137.499 134.957 136.988 135.973 135.973C138.009 133.936 138.009 130.644 135.973 128.608ZM61.9787 101.041C40.4372 101.041 22.9166 83.52 22.9166 61.9785C22.9166 40.4369 40.4372 22.9164 61.9787 22.9164C83.5202 22.9164 101.041 40.4369 101.041 61.9785C101.041 83.52 83.52 101.041 61.9787 101.041Z"
                        fill="#DDE6F3"
                    />
                    <g clip-path="url(#clip1_24531_66228)">
                        <path
                            d="M48.0296 78.3482C47.154 78.3991 46.2929 78.108 45.6278 77.5363C44.3161 76.2168 44.3161 74.0858 45.6278 72.7664L74.3484 44.0457C75.7126 42.7691 77.8533 42.8401 79.1299 44.2043C80.2842 45.4379 80.3515 47.3341 79.2874 48.6464L50.3975 77.5363C49.7411 78.0997 48.8937 78.3903 48.0296 78.3482Z"
                            fill="#DDE6F3"
                        />
                        <path
                            d="M76.7172 78.3477C75.8297 78.3439 74.9792 77.9916 74.3491 77.3667L45.6284 48.6458C44.4132 47.2268 44.5784 45.0912 45.9974 43.876C47.264 42.7913 49.1318 42.7913 50.3982 43.876L79.2881 72.5967C80.652 73.8736 80.7225 76.0144 79.4456 77.3783C79.3948 77.4325 79.3423 77.485 79.2881 77.5358C78.9381 77.8401 78.5293 78.0692 78.087 78.2089C77.6447 78.3486 77.1785 78.3958 76.7172 78.3477Z"
                            fill="#DDE6F3"
                        />
                    </g>
                    <g filter="url(#filter0_f_24531_66228)">
                        <ellipse cx="64.5" cy="138" rx="50" ry="4.5" fill="#DDE6F3" />
                    </g>
                </g>
                <defs>
                    <filter
                        id="filter0_f_24531_66228"
                        x="4.5"
                        y="123.5"
                        width="120"
                        height="29"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="5" result="effect1_foregroundBlur_24531_66228" />
                    </filter>
                    <clipPath id="clip0_24531_66228">
                        <rect width="150" height="150" fill="white" />
                    </clipPath>
                    <clipPath id="clip1_24531_66228">
                        <rect width="35.7143" height="35.7143" fill="white" transform="translate(44.6431 42.8574)" />
                    </clipPath>
                </defs>
            </svg>

            <Typography variant="h4" fontWeight={500} style={styles.emptyTitle}>
                No Results Found
            </Typography>
            <Typography variant="h6" sx={styles.emptySubtitle}>
                Please clear filters and try again.
            </Typography>
        </Stack>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
const AppointmentRequestTable = ({
    filterApplied = false,
    tabValue = 0,
    rows = [],
    showLoader,
    totalCount = 0,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    onSort,
    onPageChange,
    onRowsPerPageChange,
    onView,
    onViewCustomer,
    onRowClick,
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const [anchorElDropdown, setAnchorElDropdown] = React.useState(null);
    const [selectedRow, setSelectedRow] = React.useState({});
    const openDropdown = Boolean(anchorElDropdown);

    const columns = TAB_COLUMNS[tabValue] || TAB_COLUMNS[0];

    const { data: configData } = useGetShopConfig();

    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        setAnchorElDropdown(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => setAnchorElDropdown(null);

    return (
        <>
            <Grid container item sx={{ bgcolor: 'white', borderRadius: '8px', boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.10)' }}>
                <Grid item xl={12} md={12} sm={12} xs={12}>
                    <TableContainer sx={isMdUp ? styles.tableContainerMdUp : styles.tableContainerSmDown}>
                        <Table stickyHeader={!isMdUp} aria-label="appointment requests table" sx={TABLE_SX}>
                            {!filterApplied && rows.length === 0 && !showLoader ? (
                                <></>
                            ) : (
                                <TableHead>
                                    <TableRow>
                                        {columns.map((col) => (
                                            <TableCell
                                                key={col.id}
                                                align="left"
                                                sx={{ ...COL, fontWeight: 500, ...(col.minWidth ? { minWidth: col.minWidth } : {}) }}
                                            >
                                                {col.sortable ? (
                                                    <TableSortLabel
                                                        active={sortBy === col.id}
                                                        direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                        onClick={() => onSort(col.id)}
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    col.label
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={tabValue === 0 ? styles.actionColHeader100 : styles.actionColHeader}>
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                            )}

                            <TableBody>
                                {showLoader ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center" sx={styles.emptyCellLoader}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center" sx={styles.emptyCellLoader}>
                                            {filterApplied ? <EmptyResult /> : <EmptyState tabValue={tabValue} configData={configData} navigate={navigate} />}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row._id} hover sx={{ cursor: 'pointer' }}>
                                            {columns.map((col) => (
                                                <TableCell key={col.id} align="left" sx={BODY_CELL} onClick={() => onView && onView(row)}>
                                                    {renderCell(col.id, row)}
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                align="center"
                                                sx={tabValue !== 0 ? { ...BODY_CELL, ...ACTION_COL } : { ...BODY_CELL, ...ACTION_COL_100 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {tabValue === 0 ? (
                                                    <Button
                                                        sx={styles.reviewBtn}
                                                        variant="contained"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onView && onView(row);
                                                        }}
                                                    >
                                                        Review
                                                    </Button>
                                                ) : (
                                                    <Box display="flex" justifyContent="center">
                                                        <IconButton size="small" sx={styles.actionIconBtn} onClick={(e) => handleMenuOpen(e, row)}>
                                                            <MoreVert sx={styles.actionIcon} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Grid container item alignItems={'center'} justifyContent="end">
                        <Grid item>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                colSpan={3}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page > 0 ? page - 1 : 0}
                                slotProps={{
                                    select: { 'aria-label': 'rows per page' },
                                    actions: { showFirstButton: true, showLastButton: true },
                                }}
                                onPageChange={onPageChange}
                                onRowsPerPageChange={onRowsPerPageChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* ── 3-dot menu ── */}
            <Menu anchorEl={anchorElDropdown} open={openDropdown} onClose={handleMenuClose} sx={{ '& .MuiPaper-root': styles.menuPaper }}>
                <MenuItem
                    sx={styles.menuItem}
                    onClick={() => {
                        onView && onView(selectedRow);
                        handleMenuClose();
                    }}
                >
                    <ViewAppointmentIcon />
                    <span style={styles.menuItemIcon}>View Appointment</span>
                </MenuItem>
                <MenuItem
                    sx={styles.menuItem}
                    onClick={() => {
                        onViewCustomer && onViewCustomer(selectedRow);
                        handleMenuClose();
                    }}
                >
                    <Visibility />
                    <span style={styles.menuItemIcon}>View Customer</span>
                </MenuItem>
            </Menu>
        </>
    );
};

export default AppointmentRequestTable;
